import React from 'react'
import { AnchorsOfForms } from '@constants/forms'
import { Section } from '@shared/components/section'
import { Spoiler } from '@shared/components/spoiler'
import { Button } from '@shared/componentsStyled/Button'
import { Link } from '@shared/componentsStyled/Link'
import { changeDocumentHash } from '@shared/pipes/dom'
import scrollToElement from '@shared/pipes/scroll'
import { PagesPath } from 'data/pages-titles'
import styled, { CSSObject } from 'styled-components'

import { Input } from '../Input'

import {
	NAME_MAX_LENGTH,
	useSectionConsultNew,
} from './useSectionConsultNew'

export const goToConsultLink = () => {
	scrollToElement(`#${AnchorsOfForms.investConsult}`, { time: 0 }, () => {
		changeDocumentHash(AnchorsOfForms.investConsult)
	})
}

export function SectionConsultNew(): JSX.Element {
	const { isSubmitted, formik, inputDisabled, error } = useSectionConsultNew()

	return (
		<>
			<Section id={AnchorsOfForms.investConsult}>
				<ViewRoot>
					<TextTitle>Надёжные инвестиции для бизнеса</TextTitle>
					<TextSubTitle>
						Оставьте заявку на консультацию, чтобы узнать больше о продукте и быстро получить ответы
						на все интересующие вопросы
					</TextSubTitle>
					<ViewRow>
						<ViewForm>
							<form onSubmit={formik.handleSubmit}>
								<Label htmlFor='investBusinessConsultName'>Как к вам обращаться</Label>
								<Input
									id='investBusinessConsultName'
									name='name'
									type='text'
									placeholder='Имя'
									onChange={formik.handleChange}
									onBlur={formik.handleBlur}
									value={formik.values.name}
									hasError={!!formik.errors.name}
									maxLength={NAME_MAX_LENGTH}
									disabled={inputDisabled}
								/>
								<ViewInputInfo>
									{!!formik.errors.name && <TextError>{formik.errors.name}</TextError>}
								</ViewInputInfo>
								<Label htmlFor='investBusinessConsultPhone'>Номер телефона</Label>
								<Input
									mask='+7 999 999-99-99'
									id='investBusinessConsultPhone'
									name='phone'
									value={formik.values.phone}
									type='text'
									onChange={formik.handleChange}
									onBlur={formik.handleBlur}
									placeholder='Телефон'
									hasError={!!formik.errors.phone}
									disabled={inputDisabled}
								/>
								<TextNote style={{ marginTop: 14 }}>
									Нажимая кнопку «Получить консультацию» вы{' '}
									<Link href={PagesPath.agreement}>
										соглашаетесь на обработку персональных данных
									</Link>
									.
								</TextNote>
								<ViewInputInfo last>
									{!!formik.errors.phone && <TextError>{formik.errors.phone}</TextError>}
								</ViewInputInfo>
								<ButtonStyled type='submit' success={isSubmitted} disabled={inputDisabled}>
									{isSubmitted ? 'Отправлено' : 'Получить консультацию'}
								</ButtonStyled>
							</form>
							<ViewFormInfo>{!!error && <TextError>{error}</TextError>}</ViewFormInfo>
						</ViewForm>
						<ViewInfo>
							<TextInfoTitle>Почитать самостоятельно</TextInfoTitle>
							<TextInfo>
								Всё самое важное о продукте можно изучить в формате презентации.
								<br />
								<br />В презентации: основная информация о продукте, актуальные условия, рассказ о
								компании и контакты менеджера.
							</TextInfo>
							<Link
								href='https://public.credit.club/presentations/deposits_business.pdf'
								target='_blank'
								rel='noopener noreferrer nofollow'
							>
								Скачать презентацию
							</Link>
						</ViewInfo>
					</ViewRow>
				</ViewRoot>
			</Section>
			<Section>
				<ViewSpoiler>
					<noindex>
						Информация в настоящем разделе официального сайта ООО &quot;Кредит.Клаб&quot;
						https://credit.club/ об оказываемых услугах размещена лицом, оказывающем услуги и
						предназначена для информирования посетителей данного раздела сайта об ассортименте
						услуг, условиях их приобретения, ценах и правилах пользования. Услуги оператора
						инвестиционной платформы для инвесторов по тарифу «Старт» являются бесплатными в
						соответствии с Федеральным законом №259 - ФЗ от 02.08.2019 года.
						<br />
						<br />
						<Link href={PagesPath.documents}>
							Правила инвестиционной платформы ООО «Кредит.Клаб» от 23.05.2024
						</Link>
						, дата согласования Центральным банком Российской Федерации 18.06.2024, дата публикации
						на сайте оператора инвестиционной платформы 08.07.2024, дата вступления в силу
						14.07.2024.{' '}
						<Link
							href='https://www.cbr.ru/finorg/foinfo/?ogrn=1196658084743'
							target='_blank'
							rel='nofollow noopener noreferrer'
						>
							Информация о финансовой организации
						</Link>{' '}
						от Центрального банка Российской Федерации.
					</noindex>
				</ViewSpoiler>
			</Section>
		</>
	)
}

const ViewRoot = styled.div(({ theme }) => ({
	border: '1px solid rgba(216, 234, 228, 0.25)',
	borderRadius: 40,
	boxShadow: '0px 10px 30px rgba(163, 198, 186, 0.25)',
	marginBottom: 15,
	paddingTop: 20,
	paddingLeft: 20,
	paddingRight: 20,
	[theme.mediaQueries.mediaMinWidthMobile]: {
		marginBottom: 40,
		paddingTop: 40,
		paddingLeft: 60,
		paddingRight: 60,
	},
}))

const TextTitle = styled.h2(({ theme }) => ({
	...theme.typography.header30_36,
	marginBottom: 10,
	[theme.mediaQueries.mediaMinWidthMobile]: {
		...theme.typography.header56_60,
		marginBottom: 15,
	},
}))

const ViewSpoiler = styled.div(({ theme }) => ({
	...theme.typography.text12_16,
	opacity: 0.5,
	marginBottom: 50,
	[theme.mediaQueries.mediaMinWidthMobile]: {
		marginBottom: 100,
	},
}))

const TextSubTitle = styled.p(({ theme }) => ({
	...theme.typography.text18_24,
	marginBottom: 20,
	[theme.mediaQueries.mediaMinWidthMobile]: {
		...theme.typography.text24_32,
		marginBottom: 30,
	},
}))

const ViewRow = styled.div(
	({ theme }) =>
		({
			display: 'flex',
			flexDirection: 'column',
			marginBottom: 20,
			[theme.mediaQueries.mediaMinWidthMobile]: {
				flexDirection: 'row',
				marginBottom: 40,
			},
		} as CSSObject)
)

const ViewForm = styled.div(
	({ theme }) =>
		({
			position: 'relative',
			flex: 1,
			marginRight: 0,
			marginBottom: 40,
			[theme.mediaQueries.mediaMinWidthMobile]: {
				marginRight: 80,
				marginBottom: 0,
			},
		} as CSSObject)
)

const ViewInfo = styled.div({
	flex: 1,
})

const Label = styled.label(({ theme }) => ({
	opacity: 0.5,
	display: 'block',
	...theme.typography.text14_18,
	marginBottom: 5,
	[theme.mediaQueries.mediaMinWidthMobile]: {
		...theme.typography.text18_24,
		marginBottom: 10,
	},
}))

interface ViewInputInfoProps {
	last?: boolean | undefined
}
const ViewInputInfo = styled.div<ViewInputInfoProps>(({ theme, last }) => ({
	minHeight: last ? 20 : 10,
	[theme.mediaQueries.mediaMinWidthMobile]: {
		minHeight: last ? 30 : 20,
	},
}))

const ViewFormInfo = styled.div(
	({ theme }) =>
		({
			position: 'absolute',
			bottom: -18,
			left: 0,
			[theme.mediaQueries.mediaMinWidthMobile]: {
				bottom: -20,
			},
		} as CSSObject)
)

const TextError = styled.p(({ theme }) => ({
	...theme.typography.text12_16,
	color: theme.colors.error,
	marginBottom: 2,
	[theme.mediaQueries.mediaMinWidthMobile]: {
		...theme.typography.text14_18,
	},
}))

const TextInfoTitle = styled.h3(({ theme }) => ({
	marginBottom: 8,
	...theme.typography.text20_26,
	fontWeight: 'bold',
	[theme.mediaQueries.mediaMinWidthMobile]: {
		...theme.typography.text24_32,
	},
}))

const TextInfo = styled.p(({ theme }) => ({
	...theme.typography.text16_22,
	marginBottom: 10,
	[theme.mediaQueries.mediaMinWidthMobile]: {
		...theme.typography.text18_24,
		marginBottom: 20,
	},
}))

const TextNote = styled.p(({ theme }) => ({
	opacity: 0.5,
	...theme.typography.text12_16,
	marginBottom: 20,
	[theme.mediaQueries.mediaMinWidthMobile]: {
		marginBottom: 0,
	},
}))

interface ButtonStyledProps {
	success?: boolean | undefined
}
const ButtonStyled = styled(Button)<ButtonStyledProps>(({ success }) =>
	!success
		? {}
		: {
				backgroundColor: '#49D4A7',
				'&:hover': {
					backgroundColor: '#49D4A7',
				},
				'&:active': {
					backgroundColor: '#49D4A7',
				},
		  }
)
